"use client";

import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useState,
} from "react";
import { Grid } from "@mui/material";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import ReCAPTCHA from "react-google-recaptcha";

import {
  RecaptchaContextType,
  RecaptchaHttpRequest,
  RecaptchaResponse,
} from "@/types/recaptcha";

import { useUi } from "@/context/util/ui";

const RecaptchaContext = createContext<RecaptchaContextType>(null!);

export function RecaptchaProvider({ children }: { children: ReactNode }) {
  const { setLoading } = useUi();
  const { executeRecaptcha } = useGoogleReCaptcha();

  const [showV2, setShowV2] = useState(false);
  const [v2Callback, setV2Callback] = useState<
    ((token: string) => void) | null
  >(null);

  // v2 fallback logic
  async function waitForV2<T>(
    requestFactory: () => RecaptchaHttpRequest<T>
  ): Promise<T | undefined> {
    return new Promise((resolve) => {
      // this state flips UI to show the v2 widget
      setShowV2(true);

      // when v2 completes, we get its token
      setV2Callback(() => async (v2Token: string) => {
        setLoading(true);

        const request = requestFactory(); // <- new closure capturing latest state if params change between v3 fail and v2 run
        const init: RequestInit = {
          headers: {
            "X-RECAPTCHA-TOKEN": encodeURIComponent(v2Token),
            "X-RECAPTCHA-VERSION": "v2",
          },
        };
        const response = await request(init);

        const recaptchaResponse = response as RecaptchaResponse;

        if (recaptchaResponse && recaptchaResponse.valid === false)
          console.error(
            `Recaptcha v2 attempt with token ${v2Token} failed:`,
            recaptchaResponse
          );
        else if (response) resolve(response as T);

        setShowV2(false);
        setLoading(false);
      });
    });
  }

  const recaptchaRequest = useCallback(
    async <T,>(
      action: string,
      request: RecaptchaHttpRequest<T>
    ): Promise<T | undefined> => {
      if (!executeRecaptcha) {
        // recaptcha not ready → optionally fallback to v2
        return await waitForV2<T>(() => request);
      }

      let v3Token = "";

      try {
        // v3 attempt
        v3Token = await executeRecaptcha(action);

        const init: RequestInit = {
          headers: {
            "X-RECAPTCHA-TOKEN": encodeURIComponent(v3Token),
            "X-RECAPTCHA-VERSION": "v3",
          },
        };

        const response = await request(init);
        const recaptchaResponse = response as RecaptchaResponse;

        if (
          recaptchaResponse &&
          (recaptchaResponse.valid === true ||
            recaptchaResponse.suspicious === true)
        )
          // v3 suspicious → fallback to v2
          return await waitForV2<T>(() => request);

        if (response)
          // v3 valid → return downstream provider response
          return response as T;
      } catch (err) {
        console.error(
          `Recaptcha v3 attempt with token ${v3Token} failed:`,
          err
        );
        // fallback to v2 if v3 fails
        return await waitForV2<T>(() => request);
      }
    },
    [executeRecaptcha, waitForV2]
  );

  return (
    <RecaptchaContext.Provider
      value={{ executeRecaptcha, recaptchaRequest, showV2 }}
    >
      {children}
      {showV2 && v2Callback && (
        <Grid container justifyContent="flex-end">
          <ReCAPTCHA
            sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_V2_SITE_KEY!}
            onChange={(token) => (token ? v2Callback(token) : null)}
          />
        </Grid>
      )}
    </RecaptchaContext.Provider>
  );
}

export function useRecaptcha() {
  const ctx = useContext(RecaptchaContext);
  if (!ctx)
    throw new Error("useRecaptcha must be used within a RecaptchaContext");
  return ctx;
}
